
import { Vue, Options, prop } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import { playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import ModalTermCondition from "../terms-condition/index.vue";
import LogProcessScanPanel from "@/app/ui/views/shipment/booking/create-booking-v4/components/log-process-scan-panel/index.vue";
import { LogProsesScan } from "@/domain/entities/BookingV4";
import { isWithinTokenLimit, MAX_SCAN_TOKENS, tokenizeShipmentInput } from "./tokenizer";

class Props {
  modelValue = prop<string[]>({
    default: () => [],
    type: Array
  });
  isLoading = prop<boolean>({
    default: false
  });
  scanLogEntries = prop<LogProsesScan[]>({
    default: () => [],
    type: Array
  });
}
@Options({
  emits: ["update:modelValue", "scanShipment", "instantBookingChange"],
  components: {
    OverlayPanel,
    ModalTermCondition,
    LogProcessScanPanel
  }
})
class ScanBookingShipment extends Vue.with(Props) {
  inputValue = "";
  duplicateError = "";

  isShowModalTermCondition = false;
  isInstantBooking = false;

  mounted() {
    this.focusInput();
  }

  onChangeToggle() {
    if (!this.isInstantBooking) {
      this.isShowModalTermCondition = true;
      return;
    }
    this.isInstantBooking = !this.isInstantBooking;
    this.$emit("instantBookingChange", this.isInstantBooking);
  }
  onConfirm() {
    this.isShowModalTermCondition = false;
    this.isInstantBooking = true;
    this.$emit("instantBookingChange", this.isInstantBooking);
  }
  normalizeInputValue(value: string): string {
    return (value || "").toUpperCase().replace(/#/g, "");
  }

  onInputValue(event: Event) {
    const target = event.target as HTMLInputElement;
    this.inputValue = this.normalizeInputValue(target.value);
  }

  parseInputValue(value: string): string {
    return this.normalizeInputValue(value).trim();
  }

  onKeyDown(event: KeyboardEvent) {
    if (![",", " "].includes(event.key)) return;
    event.preventDefault();

    const value = this.parseInputValue(this.inputValue);
    this.inputValue = "";
    if (!value) return;

    this.tryAddChip(value, [...this.modelValue]);
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData("text") || "";
    const tokens = tokenizeShipmentInput(text)
      .flatMap((token) => token.split(/[,;]+/))
      .map((token) => this.parseInputValue(token))
      .filter(Boolean);

    this.duplicateError = "";
    this.inputValue = "";

    let chips = [...this.modelValue];
    let isExceededLimit = false;

    tokens.forEach((token) => {
      if (chips.length >= MAX_SCAN_TOKENS) {
        isExceededLimit = true;
        return;
      }
      chips = this.tryAddChip(token, chips, { silent: true });
    });

    if (isExceededLimit) {
      this.duplicateError = `Maximum of ${MAX_SCAN_TOKENS} shipments allowed.`
    }
  }

  private tryAddChip(
    value: string,
    currentChips: string[],
    options: { silent?: boolean } = {}
  ): string[] {
    if (currentChips.length >= MAX_SCAN_TOKENS) {
      if (!options.silent) {
        this.duplicateError = `Maximum of ${MAX_SCAN_TOKENS} shipments allowed.`
      }
      return currentChips;
    }

    if (currentChips.includes(value)) {
      if (!options.silent) {
        this.duplicateError = `Shipment ID ${value} sudah ada di dalam daftar`;
      }
      return currentChips;
    }

    this.duplicateError = "";
    const updated = [...currentChips, value];
    this.$emit("update:modelValue", updated);
    return updated;
  }

  removeChip(index: number) {
    this.duplicateError = "";
    this.$emit(
      "update:modelValue",
      this.modelValue.filter((_, i) => i !== index)
    );
  }

  focusInput() {
    const refs: any = this.$refs;
    refs["input_shipment_id_create_shipment"]?.focus();
  }

  toggleTooltip(event: any) {
    const refs: any = this.$refs;
    refs["opTooltip"].toggle(event);
  }

  async fetchShipmentId() {
    MainAppController.closeErrorMessage();
    this.duplicateError = "";
    if (this.isLoading || !this.modelValue.length) return;
    const values = [...this.modelValue];
    if (!isWithinTokenLimit(values))  {
      this.duplicateError = `Maximum of ${MAX_SCAN_TOKENS} shipments allowed.`
      playNotification("error");
      return;
    }
    this.$emit("update:modelValue", []);
    try {
      const callback = (error?: any) => {
        if (error) {
          playNotification("error");
          MainAppController.showErrorMessage(
            parsingErrorResponse(
              error,
              this.$t("bookingShipment.appsMarketplace.errorAddFailed"),
              () => this.fetchShipmentId()
            )
          );
          return;
        }
        playNotification("success");
        this.focusInput();
      };
      this.$emit("scanShipment", values, callback);
    } catch (error) {
      playNotification("error");
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("bookingShipment.appsMarketplace.errorAddFailed"),
          () => this.fetchShipmentId()
        )
      );
    }
  }

  async onEnter() {
    const value = this.parseInputValue(this.inputValue);
    let isAddSuccess = true;
    if (value) {
      const currentChips = [...this.modelValue];
      const updatedChips = this.tryAddChip(value, currentChips);
      isAddSuccess = updatedChips.length > currentChips.length;
    }
    this.inputValue = "";
    if (!isAddSuccess) return;
    await this.$nextTick();
    if (!this.modelValue.length) return;
    this.fetchShipmentId();
  }
}

export default ScanBookingShipment;
